// src/app/file-drop-upload/file-drop-upload.component.ts

import {
  Component,
  EventEmitter,
  Input,
  Output,
  HostListener,
  signal,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';

import { FormGroup } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { Menu } from 'primeng/menu';
import { MenuModule } from 'primeng/menu';
import { DialogModule } from 'primeng/dialog';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';

import { ApiResponse } from '../../_service/data.service';
import { Attachment, FileUploadService } from '../../_service/file-upload.service';
import { openOrDownloadAttachment } from '../../_util/attachment-open.util';

interface FileEntry {
  _id: string;
  file: File;
  name: string;
  size: number;
  addedAt: Date;
  status: string;
  isValid: boolean;
  errorMessage?: string;
}

@Component({
  standalone: true,
  selector: 'app-file-upload',
  imports: [FormsModule, CommonModule, ButtonModule, MenuModule, DialogModule],
  templateUrl: './file-upload.component.html',
  // styleUrl: './file-upload.component.css',
})
export class FileUploadComponent implements OnInit, OnChanges {
  @Input() noteForm!: FormGroup;
  @Input() noteId: string = ''; // the note to which files are attached
  @Input() spaceId?: string; // the Space the note lives in, so attachments land in the right CouchDB database
  @Input() noteAttachments: any[] | null | undefined = [];
  @Input() readOnly: boolean = false;
  // @Output() filesChanged = new EventEmitter<FileEntry[]>();
  @Output() filesValid = new EventEmitter<File[]>();
  /** The number of attachments actually being displayed (excludes soft-deleted-but-unsaved
   * entries), emitted after every load/add/remove. This is the authoritative count — parents
   * should use it for tab labels/badges instead of the note document's own `Attachments` array
   * length, which can drift out of sync with what the backend/IndexedDB actually has for this
   * note (see note/note-view.component.ts, note/note-edit.component.ts). */
  @Output() countChange = new EventEmitter<number>();
  /** Fired whenever an attachment change is staged locally (upload added, rename, delete) and still has
   * to be sent to the server via saveChanges(). The editor uses it to mark the note dirty (so the
   * unsaved-changes guards protect it) and, for renames/deletes, to send it right away. */
  @Output() staged = new EventEmitter<'upload' | 'rename' | 'delete'>();
  /** Attachments added outside this panel in the current edit session (images inserted into the note
   * text via Insert Image / drag & drop) that the server's list for the note doesn't include yet. */
  @Input() extraAttachments: Attachment[] = [];
  /** Id of an attachment the user removed here, so the editor can drop the note's images showing it. */
  @Output() attachmentRemoved = new EventEmitter<string>();

  fileEntries: FileEntry[] = []; // attachments loaded from server PLUS changes made in this edit session
  attachments: Attachment[] = []; // attachments loaded from server

  fileInfos?: Observable<any>; // confirm if this is really needed (May 2025)

  fileMenuItems: MenuItem[] = [];
  selectedFileEntry: FileEntry | null = null;
  showRenameDialog = false;
  newFileName: string = '';
  fileToRename: FileEntry | null = null;
  renameErrorMsg: string = '';
  private lastLoadedNoteId: string = '';

  dragOver = signal(false);
  errorMsg: string | null = null;

  readonly maxSize = 10 * 1024 * 1024; // 10MB
  readonly allowedTypes = [
    'text/plain',
    'application/pdf',
    'image/jpeg',
    'image/png',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  ];

  constructor(private messageService: MessageService, private uploadService: FileUploadService) {}

  ngOnInit(): void {
    console.log('***In file-upload-component: ngOnInit()');
    console.log('   noteID : ', this.noteId);
    console.log('   read-only status : ', this.readOnly);
    this.initializeMenus();
    this.loadAttachmentsIfNeeded();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['readOnly'] && !changes['readOnly'].firstChange) {
      this.initializeMenus();
    }

    if (changes['noteId']) {
      this.loadAttachmentsIfNeeded();
    }

    if (changes['extraAttachments'] && !changes['extraAttachments'].firstChange) {
      this.mergeExtraAttachments();
      this.emitCount();
    }
  }

  /** Adds extraAttachments not listed yet. Queued ones (offline / unsaved note, `att-pending:` id) are
   * not on the server, so they're marked 'queued' rather than 'saved'. */
  private mergeExtraAttachments(): void {
    for (const att of this.extraAttachments ?? []) {
      if (!att?._id || this.fileEntries.some((e) => e._id === att._id)) continue;
      const name = att.filename || att._id;
      this.fileEntries.push({
        _id: att._id,
        file: new File([], name),
        name,
        size: Number(att.size) || 0,
        addedAt: new Date(),
        status: FileUploadService.isQueuedAttachmentId(att._id) ? 'queued' : 'saved',
        isValid: true,
      });
    }
  }

  private loadAttachmentsIfNeeded(): void {
    const incomingNoteId = (this.noteId ?? '').trim();

    // Guard against async template binding phases where note id is briefly unavailable.
    if (!incomingNoteId) {
      this.attachments = [];
      this.fileEntries = [];
      this.lastLoadedNoteId = '';
      return;
    }

    if (this.lastLoadedNoteId === incomingNoteId) {
      return;
    }

    this.lastLoadedNoteId = incomingNoteId;
    this.loadAttachments();
  }

  convertAttachmentsToFileEntries(attachments: Attachment[]): FileEntry[] {
    return attachments.map((att: any) => {
      const fileName = String(att?.filename ?? att?.fileName ?? att?.FileName ?? att?.name ?? att?.Name ?? att?._id);
      const createdOn = att?.created_on ?? att?.createdOn ?? att?.created ?? att?.uploaded_on ?? att?.UploadDatum;
      const size = Number(att?.size ?? att?.Size ?? 0);

      return {
        _id: String(att?._id ?? att?.id ?? att?.attachmentId ?? fileName),
        file: new File([], fileName), // Placeholder file object with empty content
        name: fileName,
        size: Number.isFinite(size) ? size : 0,
        addedAt: createdOn ? new Date(createdOn) : new Date(),
        status: 'stored',
        isValid: true,
      };
    });
  }

  private mapNoteAttachmentFallbackToFileEntries(rawAttachments: any[] | null | undefined): FileEntry[] {
    if (!Array.isArray(rawAttachments) || rawAttachments.length === 0) {
      return [];
    }

    return rawAttachments
      .map((att: any) => {
        if (!att) {
          return null;
        }

        if (typeof att === 'string') {
          return {
            _id: att,
            file: new File([], att),
            name: att,
            size: 0,
            addedAt: new Date(),
            status: 'stored',
            isValid: true,
          } as FileEntry;
        }

        const id = att._id || att.id || att.attachmentId || att.filename || att.fileName || att.FileName || att.name || att.Name;
        const name = att.filename || att.fileName || att.FileName || att.name || att.Name || id || 'Attachment';
        const size = Number(att.size ?? att.Size ?? 0);
        const createdOn = att.created_on || att.createdOn || att.created || att.modified_on || att.uploaded_on || att.UploadDatum;

        if (!id) {
          return null;
        }

        return {
          _id: String(id),
          file: new File([], String(name)),
          name: String(name),
          size: Number.isFinite(size) ? size : 0,
          addedAt: createdOn ? new Date(createdOn) : new Date(),
          status: 'stored',
          isValid: true,
        } as FileEntry;
      })
      .filter((entry): entry is FileEntry => !!entry);
  }

  /** The count parents should show in a tab label/badge — excludes entries soft-deleted in this
   * edit session (status === 'deleted') but not yet saved, since those are already hidden from
   * the table below (`*ngIf="file.status !== 'deleted'"`). */
  private emitCount(): void {
    this.countChange.emit(this.fileEntries.filter((e) => e.status !== 'deleted').length);
  }

  private enrichFallbackFileNames(entries: FileEntry[]): void {
    const unresolvedEntries = entries.filter(
      (entry) => !!entry._id && (!entry.name || entry.name === entry._id),
    );

    for (const entry of unresolvedEntries) {
      this.uploadService.getAttachmentInfo(entry._id).subscribe((meta) => {
        const resolvedName =
          (meta as any)?.filename ||
          (meta as any)?.fileName ||
          (meta as any)?.FileName ||
          (meta as any)?.name ||
          (meta as any)?.Name;
        if (resolvedName) {
          entry.name = String(resolvedName);
        }
      });
    }
  }

  loadAttachments(): void {
    if (!this.noteId?.trim()) {
      this.attachments = [];
      this.fileEntries = [];
      this.emitCount();
      return;
    }

    this.uploadService.getFiles(this.noteId, this.spaceId).subscribe({
      next: (data: Attachment[]) => {
        console.log('Attachments loaded:', data);
        if (Array.isArray(data) && data.length > 0) {
          this.attachments = data;
          this.fileEntries = this.convertAttachmentsToFileEntries(this.attachments);
        } else {
          this.attachments = [];
          this.fileEntries = this.mapNoteAttachmentFallbackToFileEntries(this.noteAttachments);
        }
        // Either path can hand back an entry whose name is really just its id (a real attachment
        // record with no filename field, or the note.Attachments-id-only fallback) — try to
        // resolve a proper name for those regardless of which path produced them.
        this.enrichFallbackFileNames(this.fileEntries);
        this.mergeExtraAttachments();
        this.emitCount();
      },
      error: (err) => {
        console.error('Error fetching attachments:', err);
        this.attachments = [];
        this.fileEntries = this.mapNoteAttachmentFallbackToFileEntries(this.noteAttachments);
        this.enrichFallbackFileNames(this.fileEntries);
        this.mergeExtraAttachments();
        this.emitCount();
      },
    });
  }

  @HostListener('dragover', ['$event'])
  onDragOver(evt: DragEvent) {
    if (!this.readOnly) {
      evt.preventDefault();
      this.dragOver.set(true);
    }
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(evt: DragEvent) {
    evt.preventDefault();
    this.dragOver.set(false);
  }

  @HostListener('drop', ['$event'])
  onDrop(evt: DragEvent) {
    if (this.readOnly) return; // exit in read-only mode!
    evt.preventDefault();
    this.dragOver.set(false);
    this.errorMsg = null;

    const files = Array.from(evt.dataTransfer?.files ?? []);
    const entries: FileEntry[] = [];

    for (const file of files) {
      const basicValid = this.allowedTypes.includes(file.type) && file.size <= this.maxSize;

      let isValid = basicValid;
      let errorMessage: string | undefined;

      if (!basicValid) {
        errorMessage = !this.allowedTypes.includes(file.type)
          ? `Invalid file type: ${file.name}`
          : `File too large: ${file.name}`;
      } else {
        const nameValidation = this.validateFileName(file.name);
        if (!nameValidation.valid) {
          isValid = false;
          errorMessage = nameValidation.error;
        }
      }

      const entry: FileEntry = {
        _id: '',
        file: file,
        name: file.name,
        size: file.size,
        addedAt: new Date(),
        status: isValid ? 'new' : 'invalid',
        isValid,
        errorMessage,
      };

      entries.push(entry);
    }

    this.fileEntries.push(...entries);
    this.emitCount();

    const validFiles = entries.filter((e) => e.isValid).map((e) => e.file);
    if (validFiles.length > 0) {
      this.filesValid.emit(validFiles);
      this.staged.emit('upload');
    }
  }

  onFileInputChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;

    const fakeDropEvent = {
      dataTransfer: { files: input.files },
      preventDefault: () => {},
    } as DragEvent;

    this.onDrop(fakeDropEvent);
  }

  /** True for an attachment that exists on the server: 'stored' = loaded with the note, 'saved' = uploaded or
   * changed in this session, 'renamed' = on the server under its old name. Only these need a server call. */
  private isOnServer(entry: FileEntry): boolean {
    return entry.status === 'stored' || entry.status === 'saved' || entry.status === 'renamed';
  }

  removeFile(entry: FileEntry) {
    const previousStatus = entry.status;
    if (this.isOnServer(entry) && !FileUploadService.isQueuedAttachmentId(entry._id)) {
      entry.status = 'deleted';
      console.log(
        '[Attachments] DELETE staged locally — the editor sends it right away (or on the next flush if that fails / offline)',
        { attachmentId: entry._id, name: entry.name, previousStatus, noteId: this.noteId, spaceId: this.spaceId },
      );
      this.emitCount();
      this.staged.emit('delete');
      this.attachmentRemoved.emit(entry._id);
      return;
    } else if (FileUploadService.isQueuedAttachmentId(entry._id)) {
      // Stored only locally, waiting for the sync to upload it: drop the queued copy instead.
      this.fileEntries = this.fileEntries.filter((e) => e !== entry);
      this.uploadService.discardQueuedAttachment(entry._id);
      this.attachmentRemoved.emit(entry._id);
      this.staged.emit('delete');
    } else {
      this.fileEntries = this.fileEntries.filter((e) => e !== entry);
      console.log('[Attachments] DELETE of a not-yet-uploaded file — dropped locally, no server call needed', {
        name: entry.name,
        previousStatus,
        noteId: this.noteId,
      });
    }
    this.emitCount();
    // this.filesChanged.emit(this.getValidFiles());
  }

  initializeMenus(): void {
    const items: MenuItem[] = [
      {
        label: 'Download',
        icon: 'bi bi-download',
        styleClass: 'text-red-500',
        command: () => this.onSelectFileMenuItem('Download', this.selectedFileEntry),
      },
    ];

    if (!this.readOnly) {
      items.push(
        {
          label: 'Rename',
          icon: 'bi bi-pencil',
          styleClass: 'text-red-500',
          command: () => this.onSelectFileMenuItem('Rename', this.selectedFileEntry),
        },
        {
          label: 'Delete',
          icon: 'bi bi-trash-fill',
          styleClass: 'text-red-500',
          command: () => this.onSelectFileMenuItem('Delete', this.selectedFileEntry),
        },
      );
    }

    this.fileMenuItems = [{ items }];
  }

  showFileMenu(event: Event, menu: Menu, file: FileEntry): void {
    this.selectedFileEntry = file;
    menu.toggle(event);
  }

  onSelectFileMenuItem(action: string, file: FileEntry | null) {
    if (!file) return;

    switch (action) {
      case 'Download':
        console.log('Download:', file.name);
        this.onDownloadFile();
        break;
      case 'Rename':
        // console.log("Rename:", file.name);
        this.fileToRename = file;
        this.newFileName = file.name;
        this.showRenameDialog = true;
        break;
      case 'Delete':
        console.log('Delete:', file.name);
        this.removeFile(file);
        break;
    }
  }

  private validateFileName(name: string, excludeEntry?: FileEntry): { valid: boolean; error?: string } {
    const trimmed = name.trim();

    if (!trimmed) return { valid: false, error: 'Der Dateiname darf nicht leer sein.' };
    if (trimmed.length > 100) return { valid: false, error: 'Der Dateiname ist zu lang.' };
    if (/[\/\\<>:"|?*\x00-\x1F]/.test(trimmed))
      return {
        valid: false,
        error: 'Der Dateiname enthält ungültige Zeichen.',
      };
    if (trimmed.includes('..') || trimmed.includes('/'))
      return { valid: false, error: 'Ungültiger Pfad im Dateinamen.' };

    const duplicate = this.fileEntries.some(
      (entry) => entry !== excludeEntry && entry.name.toLowerCase() === trimmed.toLowerCase(),
    );

    if (duplicate)
      return {
        valid: false,
        error: 'Eine Datei mit diesem Namen existiert bereits.',
      };

    return { valid: true };
  }

  closeRename(): void {
    this.showRenameDialog = false;
    this.fileToRename = null;
    this.newFileName = '';
    this.renameErrorMsg = '';
  }

  confirmRename(): void {
    const trimmedName = this.newFileName.trim();

    if (!this.fileToRename) {
      this.closeRename();
      return;
    }

    const result = this.validateFileName(trimmedName, this.fileToRename);

    if (!result.valid) {
      this.renameErrorMsg = result.error!;
      return;
    }

    const previousName = this.fileToRename.name;
    const previousStatus = this.fileToRename.status;
    this.fileToRename.name = trimmedName;
    if (this.isOnServer(this.fileToRename)) this.fileToRename.status = 'renamed';
    console.log(
      this.fileToRename.status === 'renamed'
        ? '[Attachments] RENAME staged locally — the editor sends it right away (or on the next flush if that fails / offline)'
        : '[Attachments] RENAME of a file that is not a saved attachment — only the local name changed',
      {
        attachmentId: this.fileToRename._id,
        previousName,
        newName: trimmedName,
        previousStatus,
        status: this.fileToRename.status,
        noteId: this.noteId,
        spaceId: this.spaceId,
      },
    );
    if (this.fileToRename.status === 'renamed') this.staged.emit('rename');

    this.closeRename();
  }

  formatFileSize(bytes?: number | null): string {
    if (!bytes || isNaN(bytes)) return '';

    if (!bytes) bytes = 0;
    if (bytes < 1024) return `${bytes} B`;
    const units = ['KB', 'MB', 'GB'];
    let size = bytes / 1024;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    const rounded = size < 10 ? size.toFixed(1) : Math.round(size).toString();
    return `${rounded} ${units[unitIndex]}`;
  }

saveChanges(): Observable<ApiResponse<{
  AttachmentsAdded: string[];
  AttachmentsRenamed: string[];
  AttachmentsDeleted: string[];
}>> {
  console.debug('## In file-upload-component.saveChanges()');

  const newFiles = this.fileEntries.filter((e) => e.status === 'new' && e.isValid);
  const renamedFiles = this.fileEntries.filter((e) => e.status === 'renamed' && e.isValid);
  const deletedFiles = this.fileEntries.filter((e) => e.status === 'deleted');
  console.log('[Attachments] saveChanges() — pending operations', {
    noteId: this.noteId,
    spaceId: this.spaceId,
    uploads: newFiles.map((e) => e.name),
    renames: renamedFiles.map((e) => ({ attachmentId: e._id, newName: e.name })),
    deletes: deletedFiles.map((e) => ({ attachmentId: e._id, name: e.name })),
    allEntries: this.fileEntries.map((e) => ({ attachmentId: e._id, name: e.name, status: e.status, isValid: e.isValid })),
  });

  const uploadTasks: Observable<ApiResponse<Attachment>>[] = [];
  const renameTasks: Observable<ApiResponse<Attachment>>[] = [];
  const deleteTasks: Observable<ApiResponse<null>>[] = [];

  // 🧩 Arrays to collect affected IDs
  const addedIds: string[] = [];
  const renamedIds: string[] = [];
  const deletedIds: string[] = [];

  const makeError = <T>(message: string, code = 500): ApiResponse<T> => ({
    status: 'error',
    error: { code, message },
  });

  // ✅ Upload new files
  for (const entry of newFiles) {
    uploadTasks.push(
      this.uploadService.uploadAttachment(entry.file, this.noteId, this.spaceId).pipe(
        map((response) => {
          if (response.status === 'success' && response.data?._id) {
            entry.status = 'saved';
            addedIds.push(response.data._id);

            // ✅ Update attachment references safely
            let currentAttachments: string[] = [];
            if (this.noteForm?.get('Attachments')) {
              currentAttachments = this.noteForm.get('Attachments')?.value ?? [];
            } else if ((this as any).note?.Attachments) {
              currentAttachments = (this as any).note.Attachments ?? [];
            }

            if (!currentAttachments.includes(response.data._id)) {
              currentAttachments.push(response.data._id);
              if (this.noteForm?.get('Attachments')) {
                this.noteForm.get('Attachments')?.setValue(currentAttachments);
              } else if ((this as any).note) {
                (this as any).note.Attachments = [...currentAttachments];
              }
            }

            console.log(`✅ Attachment ${response.data.filename} linked to note ${this.noteId}`);
          } else {
            console.warn(`⚠ Upload non-success for ${entry.name}:`, response.error?.message ?? 'unknown reason');
          }
          return response;
        }),
        catchError((err) => {
          const msg = err?.error?.error?.message || err?.message || `Error during upload: ${entry.name}`;
          console.error(`❌ Upload error for ${entry.name}:`, msg);
          return of(makeError<Attachment>(msg));
        }),
      ),
    );
  }

  // ✅ Rename existing files
  for (const entry of renamedFiles) {
    console.log('[Attachments] RENAME → sending to server', {
      attachmentId: entry._id,
      newName: entry.name,
      noteId: this.noteId,
      spaceId: this.spaceId,
    });
    renameTasks.push(
      this.uploadService.renameAttachment(entry._id, entry.name, this.noteId, this.spaceId).pipe(
        map((response) => {
          if (response.status === 'success') {
            entry.status = 'saved';
            renamedIds.push(entry._id);
            console.log(`✏️ Renamed attachment ${entry._id} to ${entry.name}`);
          } else {
            console.warn(`⚠ Rename failed for ${entry.name}:`, response.error?.message ?? 'unknown reason');
          }
          return response;
        }),
        catchError((err) => {
          const msg = err?.error?.error?.message || err?.message || `Error during rename: ${entry.name}`;
          console.error(`❌ Rename error for ${entry.name}:`, msg);
          return of(makeError<Attachment>(msg));
        }),
      ),
    );
  }

  // ✅ Delete files
  for (const entry of deletedFiles) {
    console.log('[Attachments] DELETE → sending to server', {
      attachmentId: entry._id,
      name: entry.name,
      noteId: this.noteId,
      spaceId: this.spaceId,
    });
    deleteTasks.push(
      this.uploadService.deleteAttachment(this.noteId, entry._id, this.spaceId).pipe(
        map((response) => {
          if (response.status === 'success') {
            deletedIds.push(entry._id);
            this.fileEntries = this.fileEntries.filter((e) => e !== entry);

            // Remove from note’s attachments
            const currentAttachments: string[] = this.noteForm.get('Attachments')?.value ?? [];
            const updated = currentAttachments.filter((id) => id !== entry._id);
            this.noteForm.get('Attachments')?.setValue(updated);

            console.log(`🗑 Deleted attachment ${entry._id}`);
          } else {
            console.warn(`⚠ Delete failed for ${entry.name}:`, response.error?.message ?? 'unknown reason');
          }
          return response;
        }),
        catchError((err) => {
          const msg = err?.error?.error?.message || err?.message || `Error during delete: ${entry.name}`;
          console.error(`❌ Delete error for ${entry.name}:`, msg);
          return of(makeError<null>(msg));
        }),
      ),
    );
  }

  const allTasks: Observable<ApiResponse<unknown>>[] = [...uploadTasks, ...renameTasks, ...deleteTasks];

  if (allTasks.length === 0) {
    console.debug('No file changes detected.');
    return of({
      status: 'success',
      data: {
        AttachmentsAdded: [],
        AttachmentsRenamed: [],
        AttachmentsDeleted: [],
      },
    });
  }

  return forkJoin(allTasks).pipe(
    map((results: ApiResponse<unknown>[]) => {
      const failed = results.filter((r) => r.status !== 'success');
      if (failed.length > 0) {
        const combined = failed.map((f) => f.error?.message ?? 'Unknown error').join('; ');
        console.warn('⚠ Some attachment operations failed:', combined);
        return { status: 'error', error: { code: 500, message: combined } };
      }

      console.log('✅ All attachment operations completed successfully.');
      return {
        status: 'success',
        data: {
          AttachmentsAdded: addedIds,
          AttachmentsRenamed: renamedIds,
          AttachmentsDeleted: deletedIds,
        },
      };
    }),
  );
}

  OLD___saveChanges(): Observable<ApiResponse<null>> {
    console.debug('## In file-upload-component.saveChanges()');

    const newFiles = this.fileEntries.filter((e) => e.status === 'new' && e.isValid);
    const renamedFiles = this.fileEntries.filter((e) => e.status === 'renamed' && e.isValid);
    const deletedFiles = this.fileEntries.filter((e) => e.status === 'deleted');

    const uploadTasks: Observable<ApiResponse<Attachment>>[] = [];
    const renameTasks: Observable<ApiResponse<Attachment>>[] = [];
    const deleteTasks: Observable<ApiResponse<null>>[] = [];

    const makeError = <T>(message: string, code = 500): ApiResponse<T> => ({
      status: 'error',
      error: { code, message },
    });

    // Upload new files
    for (const entry of newFiles) {
      uploadTasks.push(
        this.uploadService.uploadAttachment(entry.file, this.noteId, this.spaceId).pipe(
          map((response) => {
            if (response.status === 'success') entry.status = 'saved';
            return response;
          }),
          catchError(() => of(makeError<Attachment>(`Error during upload: ${entry.name}`))),
        ),
      );
    }

    // Rename existing files
    for (const entry of renamedFiles) {
      renameTasks.push(
        this.uploadService.renameAttachment(entry._id, entry.name, this.noteId, this.spaceId).pipe(
          map((response) => {
            if (response.status === 'success') entry.status = 'saved';
            return response;
          }),
          catchError(() => of(makeError<Attachment>(`Error during rename: ${entry.name}`))),
        ),
      );
    }

    // Delete files
    for (const entry of deletedFiles) {
      deleteTasks.push(
        this.uploadService.deleteAttachment(this.noteId, entry._id, this.spaceId).pipe(
          map((response) => {
            if (response.status === 'success') {
              this.fileEntries = this.fileEntries.filter((e) => e !== entry);
            }
            return response;
          }),
          catchError(() => of(makeError<null>(`Error during delete: ${entry.name}`))),
        ),
      );
    }

    const allTasks: Observable<ApiResponse<unknown>>[] = [...uploadTasks, ...renameTasks, ...deleteTasks];

    if (allTasks.length === 0) {
      return of({ status: 'success', data: null });
    }

    return forkJoin(allTasks).pipe(
      map((results: ApiResponse<unknown>[]) => {
        const failed = results.filter((r) => r.status !== 'success');
        if (failed.length > 0) {
          const combined = failed.map((f) => f.error?.message ?? 'Unknown error').join('; ');
          return { status: 'error', error: { code: 500, message: combined } };
        }
        return { status: 'success', data: null };
      }),
    );
  }

  saveChangesTo(saveToNoteId: string): Observable<ApiResponse<null>> {
    console.debug('## In file-upload-component.saveChangesTo()');

    const newFiles = this.fileEntries.filter((e) => e.status === 'new' && e.isValid);
    const savedFiles = this.fileEntries.filter((e) => e.status === 'saved' && e.isValid);
    const renamedFiles = this.fileEntries.filter((e) => e.status === 'renamed' && e.isValid);
    const deletedFiles = this.fileEntries.filter((e) => e.status === 'deleted');

    const uploadTasks: Observable<ApiResponse<Attachment>>[] = [];
    const copyTasks: Observable<ApiResponse<Attachment>>[] = [];
    const renameTasks: Observable<ApiResponse<Attachment>>[] = [];
    const deleteTasks: Observable<ApiResponse<null>>[] = [];

    const makeError = <T>(message: string, code = 500): ApiResponse<T> => ({
      status: 'error',
      error: { code, message },
    });

    // Upload new files to the target note
    for (const entry of newFiles) {
      uploadTasks.push(
        this.uploadService.uploadAttachment(entry.file, saveToNoteId, this.spaceId).pipe(
          map((response) => {
            if (response.status === 'success') entry.status = 'saved';
            return response;
          }),
          catchError(() => of(makeError<Attachment>(`Error during upload: ${entry.name}`))),
        ),
      );
    }

    // Copy existing saved files from current note to target note
    for (const entry of savedFiles) {
      copyTasks.push(
        this.uploadService.copyAttachment(entry._id, this.noteId, saveToNoteId).pipe(
          map((response) => {
            if (response.status === 'success') entry.status = 'saved';
            return response;
          }),
          catchError(() => of(makeError<Attachment>(`Error during copy: ${entry.name}`))),
        ),
      );
    }

    // Rename files — only in new item (not original)
    for (const entry of renamedFiles) {
      renameTasks.push(
        this.uploadService.renameAttachment(entry._id, entry.name, saveToNoteId, this.spaceId).pipe(
          map((response) => {
            if (response.status === 'success') entry.status = 'saved';
            return response;
          }),
          catchError(() => of(makeError<Attachment>(`Error during rename: ${entry.name}`))),
        ),
      );
    }

    // Delete files (if needed for cloned item cleanup)
    for (const entry of deletedFiles) {
      deleteTasks.push(
        this.uploadService.deleteAttachment(saveToNoteId, entry._id, this.spaceId).pipe(
          map((response) => {
            if (response.status === 'success') {
              this.fileEntries = this.fileEntries.filter((e) => e !== entry);
            }
            return response;
          }),
          catchError(() => of(makeError<null>(`Error during delete: ${entry.name}`))),
        ),
      );
    }

    const allTasks: Observable<ApiResponse<unknown>>[] = [...uploadTasks, ...copyTasks, ...renameTasks, ...deleteTasks];

    if (allTasks.length === 0) {
      return of({ status: 'success', data: null });
    }

    return forkJoin(allTasks).pipe(
      map((results: ApiResponse<unknown>[]) => {
        const failed = results.filter((r) => r.status !== 'success');
        if (failed.length > 0) {
          const combined = failed.map((f) => f.error?.message ?? 'Unknown error').join('; ');
          return { status: 'error', error: { code: 500, message: combined } };
        }
        return { status: 'success', data: null };
      }),
    );
  }

  onDownloadFile(): void {
    const file = this.selectedFileEntry;
    if (!file || !file._id) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No file selected',
        detail: 'Please select a file to download.',
      });
      return;
    }
    console.log('Downloading file:', file.name);
    this.uploadService.getFileForNote(this.noteId, file._id, file.name, this.spaceId).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error during download',
          detail: `File could not be downloaded: ${err.message || err}`,
        });
      },
    });
  }

  /** File name click (View → Attachments): a file the browser can show (image, PDF, text, …) opens in a
   * new window; any other file is downloaded. */
  downloadFile(entry: FileEntry): void {
    this.selectedFileEntry = entry;
    if (!entry._id) {
      this.onDownloadFile();
      return;
    }
    openOrDownloadAttachment(
      entry.name,
      entry.file?.type,
      () => this.uploadService.getFileForNote(this.noteId, entry._id, entry.name, this.spaceId),
      (err: any) =>
        this.messageService.add({
          severity: 'error',
          summary: 'Error opening file',
          detail: `File could not be opened: ${err?.message || err}`,
        }),
    );
  }

  //////////////////////////////////////////////////////////////////
  // Obsolete Functions!!
  //////////////////////////////////////////////////////////////////
}
